import React from 'react';
import { Navigate, useParams } from 'react-router';
import {
  HomePage,
  SearchPage,
  TournamentsPage,
  TournamentDetailPage,
  TournamentNewPage,
  TournamentSettingsPage,
  TeamsPage,
  TeamDetailPage,
  CreateTeamPage,
  PlayersPage,
  PlayerFormPage,
  PlayerDetailPage,
  SportsPage,
  OrganizationsPage,
  OrganizationFormPage,
  VenuesPage,
  VenueFormPage,
  OfficialsPage,
  OfficialFormPage,
  AnalyticsPage,
  MatchDetailPage,
  MatchLivePage,
  CreateMatchPage,
  SettingsPage,
  ProfilePage,
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
  AuthPage,
  AuthEmailConfirmPage,
} from '@/pages';

/** Редирект со старых адресов: `:param` в `to` подставляется из текущего маршрута. */
const RedirectTo: React.FC<{ to: string }> = ({ to }) => {
  const params = useParams();
  const target = to.replace(/:(\w+)/g, (_, key: string) => encodeURIComponent(params[key] ?? ''));
  return <Navigate to={target} replace />;
};

/**
 * Контракт маршрутов: единственный источник правды.
 *
 * - public: true  → без авторизации (просмотр, auth-страницы)
 * - public: false → редирект на /login если не авторизован (создание/редактирование)
 * - layout: true  → оборачивается в AppLayout (header + bottom nav)
 * - bare: true    → полноэкранная форма/ведение матча: без нижней панели и шапки на mobile
 */
export interface RouteConfigItem {
  path: string;
  element: React.ReactNode;
  /** Маршрут доступен без авторизации */
  public: boolean;
  /** Отрисовывать в общем layout (header, bottom nav) */
  layout: boolean;
  bare?: boolean;
}

export const routesConfig: RouteConfigItem[] = [
  // === Auth (публичные, без layout) ===
  { path: '/auth', element: <AuthPage />, public: true, layout: false },
  { path: '/auth/email/confirm', element: <AuthEmailConfirmPage />, public: true, layout: false },
  { path: '/login', element: <LoginPage />, public: true, layout: false },
  { path: '/register', element: <RegisterPage />, public: true, layout: false },
  { path: '/forgot-password', element: <ForgotPasswordPage />, public: true, layout: false },
  { path: '/reset-password', element: <ResetPasswordPage />, public: true, layout: false },
  { path: '/verify-email', element: <VerifyEmailPage />, public: true, layout: false },
  { path: '/auth/reset-password', element: <ResetPasswordPage />, public: true, layout: false },
  { path: '/auth/verify-email', element: <VerifyEmailPage />, public: true, layout: false },

  // === Просмотр (публичные, с layout) ===
  { path: '/', element: <HomePage />, public: true, layout: true },
  { path: '/search', element: <SearchPage />, public: true, layout: true },
  { path: '/tournaments', element: <TournamentsPage />, public: true, layout: true },
  { path: '/tournaments/:id', element: <TournamentDetailPage />, public: true, layout: true },

  { path: '/teams', element: <TeamsPage />, public: true, layout: true },
  { path: '/teams/:id', element: <TeamDetailPage />, public: true, layout: true },

  { path: '/players', element: <PlayersPage />, public: true, layout: true },
  { path: '/players/:id', element: <PlayerDetailPage />, public: true, layout: true },
  { path: '/players/:id/stats', element: <PlayerDetailPage />, public: true, layout: true },

  { path: '/matches/:id', element: <MatchDetailPage />, public: true, layout: true },

  { path: '/sports', element: <SportsPage />, public: true, layout: true },
  { path: '/organizations', element: <OrganizationsPage />, public: true, layout: true },
  { path: '/venues', element: <VenuesPage />, public: true, layout: true },
  { path: '/officials', element: <OfficialsPage />, public: true, layout: true },
  { path: '/analytics', element: <AnalyticsPage />, public: true, layout: true },

  // === Создание/редактирование и ведение (требуют авторизации) ===
  { path: '/tournaments/new', element: <TournamentNewPage />, public: false, layout: true, bare: true },
  { path: '/tournaments/create', element: <Navigate to="/tournaments/new" replace />, public: false, layout: false },
  { path: '/tournaments/:id/settings', element: <TournamentSettingsPage />, public: false, layout: true, bare: true },
  { path: '/tournaments/:id/edit', element: <RedirectTo to="/tournaments/:id/settings" />, public: false, layout: false },
  { path: '/tournaments/:tournamentId/matches/create', element: <CreateMatchPage />, public: false, layout: true },
  { path: '/tournaments/:tournamentId/register', element: <RedirectTo to="/tournaments/:tournamentId?tab=manage" />, public: true, layout: false },

  { path: '/matches/:id/live', element: <MatchLivePage />, public: false, layout: true, bare: true },
  { path: '/matches/:id/results', element: <RedirectTo to="/matches/:id/live" />, public: false, layout: false },

  { path: '/teams/new', element: <CreateTeamPage />, public: false, layout: true, bare: true },
  { path: '/teams/create', element: <Navigate to="/teams/new" replace />, public: false, layout: false },
  { path: '/teams/:id/edit', element: <CreateTeamPage />, public: false, layout: true, bare: true },

  { path: '/players/new', element: <PlayerFormPage />, public: false, layout: true, bare: true },
  { path: '/players/:id/edit', element: <PlayerFormPage />, public: false, layout: true, bare: true },
  { path: '/venues/new', element: <VenueFormPage />, public: false, layout: true, bare: true },
  { path: '/venues/:id/edit', element: <VenueFormPage />, public: false, layout: true, bare: true },
  { path: '/organizations/new', element: <OrganizationFormPage />, public: false, layout: true, bare: true },
  { path: '/organizations/:id/edit', element: <OrganizationFormPage />, public: false, layout: true, bare: true },
  { path: '/officials/new', element: <OfficialFormPage />, public: false, layout: true, bare: true },
  { path: '/officials/:id/edit', element: <OfficialFormPage />, public: false, layout: true, bare: true },

  { path: '/settings', element: <SettingsPage />, public: true, layout: true },
  { path: '/profile', element: <ProfilePage />, public: false, layout: true },
];
